import { Component } from '@verndale/core';
import { createFocusTrap } from 'focus-trap';
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks
} from 'body-scroll-lock';

import focusableElements from '../utils/focusable-elements';
import { isIOS } from '../utils/browsers';
import { transitionComplete } from '../utils/timedEvents';
import HeaderLinkGroup from './header-link-group';
import breakpoints from '../utils/breakpoints';
import keys from '../utils/keys';
import HeaderSearch from './header-search';

class Header extends Component {
  setupDefaults() {
    this.dom = {
      $mobile: this.el._$('.header--mobile'),
      $controlSearch: this.el._$('.header__control--search'),
      $controlMenu: this.el._$('.header__control--menu'),
      $controlClose: this.el._$('.header__control--close'),
      $mobileItemButtons: this.el._$$('.header__mobile-item-button'),
      $linkGroups: this.el._$$(
        '.header__link-group:not(.header__link-group--tablet)'
      ),
      $backButtons: this.el._$$('.header__region-back .btn-label'),
      $mobileRegions: this.el._$$('.header__mobile-item .header__region'),
      $primaryNavItems: this.el._$$('.header__primary-nav-item'),
      $primaryNavItemSearch: this.el._$('.header__primary-nav-item--search'),
      $desktopNavItems: this.el._$$(
        '.header__desktop-main .header__primary-nav-item:not(.header__primary-nav-item--search)'
      ),
      $buttonClose: this.el._$$('.header__button-close'),
      $primaryNavLinks: this.el._$$('.header__primary-nav-link'),
      $searchButtonDesktop: _$('.header__search-desktop-button'),
      $searchInputDesktop: this.el._$('#header-search-desktop'),
      $searchInputMobile: this.el._$('#header-search-mobile'),
      $dropdownList: this.el._$('.dropdown__listbox')
    };

    const self = this;
    this.mainFocusTrap = createFocusTrap(this.el, {
      escapeDeactivates: false,
      onActivate() {
        document.body.style.position = 'fixed';
        document.body.style.overflow = 'hidden';
        document.body.style.width = '100%';
        disableBodyScroll(self.dom.$mobile, {
          allowTouchMove: (el) => el.closest('.dropdown__listbox')
        });
      },
      onDeactivate() {
        document.body.removeAttribute('style');
        clearAllBodyScrollLocks();
      }
    });

    this.initSearch();
    this.detectIos();
    this.createTabletDOM();
    this.moveMainLink();
    this.dom.$linkGroups.forEach((linkGroup) => new HeaderLinkGroup(linkGroup));
  }

  addListeners() {
    const {
      $controlSearch,
      $controlMenu,
      $controlClose,
      $mobileItemButtons,
      $backButtons,
      $mobileRegions,
      $primaryNavLinks,
      $searchButtonDesktop,
      $buttonClose
    } = this.dom;

    $controlSearch?._on('click', ::this.handleControlSearch);
    $controlMenu._on('click', ::this.handleControlMenu);
    $controlClose._on('click', ::this.handleControlClose);
    $buttonClose._on('click', ::this.handleButtonClose);
    $mobileItemButtons._on('click', ::this.handleMobileItemClick);
    $backButtons._on('click', ::this.handleBackButtonsClick);
    $mobileRegions._on('keydown', ::this.handleMobileRegionKeydown);
    $primaryNavLinks._on('keydown', ::this.handlePrimaryNavLinkKeydown);
    $primaryNavLinks._on('click', ::this.handlePrimaryNavLinkClick);
    $searchButtonDesktop?._on('keydown', ::this.handlePrimaryNavLinkKeydown);
    $searchButtonDesktop?._on('click', ::this.handlePrimaryNavItemSearchClick);
    breakpoints.addBreakpointListenerLg(::this.handleResize);
    document._on('click', ::this.handleWindowClick);
    document._on('keydown', ::this.handleKeyDown);
  }

  handleMobileRegionKeydown(e) {
    e.stopPropagation();
    const backButton = e.currentTarget._$('.header__region-back .btn-label');

    if (e.keyCode === keys.escape) {
      backButton.click();
    }
  }

  handleKeyDown(e) {
    if (e.keyCode === keys.escape) {
      this.handleControlClose();
    }
  }

  handlePrimaryNavLinkClick(e) {
    e.preventDefault();
    const { $primaryNavItems } = this.dom;
    const parent = e.currentTarget.closest('.header__primary-nav-item');

    $primaryNavItems
      .filter((items) => items !== parent)
      .forEach((item) => item.removeAttribute('data-item-open'));

    if (!parent.dataset?.itemOpen) {
      parent.dataset.itemOpen = 'true';
    } else {
      parent.removeAttribute('data-item-open');
    }
  }

  handleWindowClick(e) {
    const { $primaryNavItemSearch, $searchButtonDesktop, $primaryNavItems } =
      this.dom;
    const openLabel = $searchButtonDesktop?.dataset.openLabel;

    if (
      $primaryNavItemSearch &&
      $primaryNavItemSearch.dataset.itemOpen === 'true' &&
      !e.target.closest('.header__primary-nav-item--search')
    ) {
      $searchButtonDesktop.setAttribute('aria-label', openLabel);
      $primaryNavItemSearch.removeAttribute('data-item-open');
    }

    if (!e.target.closest('.header__primary-nav-item')) {
      $primaryNavItems.forEach((item) =>
        item.removeAttribute('data-item-open')
      );
    }
  }

  handleButtonClose(e) {
    const { $primaryNavItems } = this.dom;

    $primaryNavItems.forEach((item) => item.removeAttribute('data-item-open'));
  }

  handlePrimaryNavItemSearchClick(e) {
    const { $primaryNavItems, $primaryNavItemSearch, $searchInputDesktop } =
      this.dom;
    const { openLabel, closeLabel } = e.currentTarget.dataset;
    const navItem = e.target.closest('.header__primary-nav-item');

    if (e.currentTarget.getAttribute('aria-label') === openLabel) {
      e.currentTarget.setAttribute('aria-label', closeLabel);
    } else {
      e.currentTarget.setAttribute('aria-label', openLabel);
    }

    $primaryNavItems
      .filter((item) => item !== $primaryNavItemSearch)
      .forEach((item) => item.removeAttribute('data-item-open'));

    if (navItem.dataset.itemOpen === 'true') {
      navItem.removeAttribute('data-item-open');
    } else {
      navItem.dataset.itemOpen = 'true';
    }

    transitionComplete(() => {
      if (
        e.target
          .closest('.header__primary-nav-item')
          .classList.contains('header__primary-nav-item--active')
      ) {
        $searchInputDesktop.focus();
      }
    });
  }

  handlePrimaryNavLinkKeydown(e) {
    const { $primaryNavLinks, $searchButtonDesktop } = this.dom;
    const items = [...$primaryNavLinks, $searchButtonDesktop].filter(Boolean);
    const { length } = items;
    const currentIndex = items.findIndex((link) => link === e.target);

    switch (e.keyCode) {
      case keys.home:
        e.preventDefault();
        items[0].focus();
        break;
      case keys.end:
        e.preventDefault();
        items[length - 1].focus();
        break;
      case keys.left:
        e.preventDefault();
        items[(currentIndex - 1 + length) % length].focus();
        break;
      case keys.right:
        e.preventDefault();
        items[(currentIndex + 1) % length].focus();
        break;
      default:
        break;
    }
  }

  removePrimaryNavItemsActiveState() {
    const { $primaryNavItems } = this.dom;

    $primaryNavItems.forEach((item) => item.removeAttribute('data-item-open'));
  }

  handleResize() {
    if (breakpoints.isLg()) {
      document.body.removeAttribute('style');
    } else if (this.dom.$mobileRegions) {
      this.dom.$mobileRegions.forEach((region) => {
        region
          ._$$(focusableElements)
          .forEach((el) => el.removeAttribute('tabindex'));
      });
    }
  }

  handleMobileItemClick(e) {
    const parent = e.target.closest('.header__mobile-item');
    const region = parent._$('.header__region');
    const backButton = parent._$('.header__region-back .btn-label');
    const headerFocusableElements = this.el
      ._$$(focusableElements)
      .filter((el) => !el.closest('.header__region'));

    parent.dataset.itemOpen = 'true';
    transitionComplete(() => {
      disableBodyScroll(region);
      backButton?.focus();

      if (headerFocusableElements) {
        headerFocusableElements.forEach((el) =>
          el.setAttribute('tabindex', '-1')
        );
      }
    }, 600);
  }

  handleBackButtonsClick(e) {
    const parent = e.target.closest('.header__mobile-item');
    const region = parent._$('.header__region');
    const trigger = parent._$('.header__mobile-item-button');
    const headerFocusableElements = this.el
      ._$$(focusableElements)
      .filter((el) => !el.closest('.header__region'));

    parent.removeAttribute('data-item-open');
    enableBodyScroll(region);
    if (headerFocusableElements) {
      headerFocusableElements.forEach((el) => el.removeAttribute('tabindex'));
    }
    transitionComplete(() => {
      trigger?.focus();
      disableBodyScroll(region);
    }, 600);
  }

  handleControlMenu() {
    const { $controlSearch, $controlMenu, $controlClose } = this.dom;

    this.el.classList.add('header--menu-open');
    this.mainFocusTrap.activate();
    $controlMenu.hidden = true;
    $controlClose.hidden = false;
    if ($controlSearch) {
      $controlSearch.hidden = true;
    }
    $controlClose.focus();
  }

  handleControlSearch() {
    const { $controlSearch, $controlMenu, $controlClose } = this.dom;

    this.el.classList.add('header--search-open');
    this.mainFocusTrap.activate();
    $controlMenu.hidden = true;
    $controlClose.hidden = false;
    if ($controlSearch) {
      $controlSearch.hidden = true;
    }
    $controlClose.focus();
  }

  handleControlClose() {
    const { $controlSearch, $controlMenu, $controlClose } = this.dom;

    this.el.classList.remove('header--menu-open');
    this.el.classList.remove('header--search-open');
    this.mainFocusTrap.deactivate();
    $controlMenu.hidden = false;
    if ($controlSearch) {
      $controlSearch.hidden = false;
    }
    $controlClose.hidden = true;
  }

  detectIos() {
    if (isIOS) {
      this.el.classList.add('header--ios');
    }
  }

  initSearch() {
    const { $searchInputMobile, $searchInputDesktop } = this.dom;

    if ($searchInputDesktop && $searchInputMobile) {
      new HeaderSearch(this.el, $searchInputMobile, $searchInputDesktop);
    }
  }

  createTabletDOM() {
    const { $desktopNavItems } = this.dom;

    $desktopNavItems.forEach(($item) => {
      const $regionContent = $item._$('.header__region-content');
      const $cols = $item._$$('[data-col]');
      const $additionalLinks = $item._$('.header__additional-links');

      if ($cols) {
        const columns = [...new Set($cols.map((col) => col.dataset.col))];
        const $regionContentTablet = document.createElement('div');
        $regionContentTablet.classList.add('header__region-content-tablet');

        columns
          .map((colNum) => {
            const $col = document.createElement('div');
            $col.classList.add('header__region-col-tablet');
            $cols
              .filter((c) => c.dataset.col === colNum)
              .forEach((c) => {
                const copy = c.cloneNode(true);
                $col.appendChild(copy);
              });

            return $col;
          })
          .forEach((col) => {
            const copy = col.cloneNode(true);
            $regionContentTablet.appendChild(copy);
          });

        if ($additionalLinks) {
          const additionalLinksCopy = $additionalLinks.cloneNode(true);
          $regionContentTablet.appendChild(additionalLinksCopy);
        }

        $regionContent.parentNode.insertBefore(
          $regionContentTablet,
          $regionContent
        );
      }
    });
  }

  moveMainLink() {
    const $items = _$$('.header__primary-nav-item .header__region-content');

    $items.forEach(($item) => {
      const $cols = $item._$$(
        '.header__region-col:not(.header__region-col--tablet)'
      );
      const $mainCol = $item._$('.header__region-col--tablet');
      const $mainLinkGroup = $mainCol?._$('.header__link-group');

      if ($cols.length && $mainCol && $mainLinkGroup) {
        $mainLinkGroup.classList.add('header__link-group--main');
        $cols[0].insertBefore($mainLinkGroup, $cols[0].childNodes[0]);
        $mainCol.parentNode.removeChild($mainCol);
      }
    });
  }
}

export default Header;
